import { useEffect, useMemo, useState } from "react";
import { fetchMetalPrices, calculateQuote } from "@/lib/api";
import {
  METALS,
  SERVICES,
  RING_SIZES,
  THICKNESS,
  JUMP_RING_SIZES,
  STONE_TYPES,
} from "@/lib/options";
import PriceTicker from "@/components/PriceTicker";
import ServiceCard from "@/components/ServiceCard";
import QuoteBreakdown from "@/components/QuoteBreakdown";
import ScheduleDialog from "@/components/ScheduleDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gem, RefreshCcw, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/2849742/pexels-photo-2849742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const initialForm = {
  metal: "14k",
  services: [],
  current_size: "7",
  desired_size: "7.5",
  shank_thickness: "medium",
  jump_ring_size: "medium",
  stones: { count: 0, type: "standard" },
  prong_count: 0,
  chain_breaks: 1,
};

export default function Calculator() {
  const [prices, setPrices] = useState(null);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [quote, setQuote] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);

  const metalLabel = useMemo(
    () => METALS.find((m) => m.id === form.metal)?.label ?? "",
    [form.metal]
  );

  const loadPrices = async (silent = false) => {
    try {
      setPricesLoading(true);
      const data = await fetchMetalPrices();
      setPrices(data);
      if (!silent) toast.success("Live metal prices updated");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not load live metal prices"
      );
    } finally {
      setPricesLoading(false);
    }
  };

  useEffect(() => {
    loadPrices(true);
  }, []);

  // Auto-calc on changes (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!form.services.length) {
        setQuote(null);
        setCalcError(null);
        return;
      }
      try {
        setCalcLoading(true);
        setCalcError(null);
        const payload = {
          metal: form.metal,
          services: form.services,
          current_size: form.services.includes("ring_sizing")
            ? Number(form.current_size)
            : null,
          desired_size: form.services.includes("ring_sizing")
            ? Number(form.desired_size)
            : null,
          shank_thickness: form.services.includes("ring_sizing")
            ? form.shank_thickness
            : null,
          jump_ring_size: form.services.includes("jump_ring")
            ? form.jump_ring_size
            : null,
          stones: form.services.includes("stone_reset") ? form.stones : null,
          prong_count: form.services.includes("prong")
            ? Number(form.prong_count) || 0
            : 0,
          chain_breaks: form.services.includes("chain_repair")
            ? Number(form.chain_breaks) || 1
            : 1,
        };
        const data = await calculateQuote(payload);
        setQuote(data);
      } catch (e) {
        setCalcError(
          e?.response?.data?.detail || "Failed to calculate quote"
        );
        setQuote(null);
      } finally {
        setCalcLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form]);

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
  };

  const reset = () => {
    setForm(initialForm);
    setQuote(null);
    toast("Form reset");
  };

  return (
    <div className="min-h-screen text-charcoal-900">
      {/* Top header */}
      <header className="border-b border-zinc-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand">
            <Gem
              className="text-champagne-500"
              size={26}
              strokeWidth={1.4}
            />
            <div>
              <div className="overline mt-1">Jewelry Repair Estimator</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => loadPrices(false)}
            className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-charcoal-500 hover:text-charcoal-900 transition-colors"
            data-testid="refresh-prices-btn"
          >
            <RefreshCcw size={14} strokeWidth={1.5} /> Refresh prices
          </button>
        </div>
      </header>

      {/* Live price ticker */}
      <PriceTicker prices={prices} loading={pricesLoading} />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="overline">Transparent · Bench-Accurate · Instantly Priced</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight">
            A precise estimate for the
            <span className="italic text-champagne-600"> small repairs </span>
            that keep heirlooms wearable.
          </h1>
          <p className="mt-6 max-w-xl text-base text-charcoal-500 leading-relaxed">
            Build a real-time quote against current spot prices for gold, silver
            and platinum. No ambiguity — see every gram, every minute of bench
            time, line by line.
          </p>
        </div>
        <div className="lg:col-span-5 hidden lg:block animate-fade-up">
          <div className="relative overflow-hidden border border-zinc-200 bg-white">
            <img
              src={HERO_IMG}
              alt="Diamond ring on marble"
              className="w-full h-[320px] object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/40" />
          </div>
        </div>
      </section>

      {/* Calculator grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT: form */}
          <div className="lg:col-span-8 space-y-12">
            {/* Metal selection */}
            <section data-testid="section-metal">
              <SectionHeader
                step="01"
                title="Choose the Metal"
                desc="Determines purity and live spot pricing applied to materials."
              />
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {METALS.map((m) => {
                  const active = form.metal === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, metal: m.id }))}
                      data-testid={`metal-${m.id}`}
                      className={[
                        "p-4 text-left bg-white border transition-all duration-300",
                        active
                          ? "border-champagne-500 bg-champagne-50/70"
                          : "border-zinc-200 hover:border-charcoal-900/40",
                      ].join(" ")}
                    >
                      <div className="font-serif text-lg leading-tight">
                        {m.label}
                      </div>
                      <div className="text-[11px] tracking-wider uppercase text-charcoal-400 mt-1">
                        {m.purity}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Services selection */}
            <section data-testid="section-services">
              <SectionHeader
                step="02"
                title="Select the Repairs"
                desc="Add as many services as you need. Pricing updates live."
              />
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    selected={form.services.includes(s.id)}
                    onToggle={toggleService}
                  />
                ))}
              </div>
            </section>

            {/* Detail inputs (conditional) */}
            {form.services.length > 0 && (
              <section
                data-testid="section-details"
                className="bg-white border border-zinc-200 p-6 sm:p-8 space-y-10"
              >
                <SectionHeader
                  step="03"
                  title="Repair Specifics"
                  desc="Fine-tune the parameters of your selected services."
                  inline
                />

                {form.services.includes("ring_sizing") && (
                  <DetailGroup label="Ring Sizing" testid="group-ring-sizing">
                    <Field label="Current finger size">
                      <Select
                        value={form.current_size}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, current_size: v }))
                        }
                      >
                        <SelectTrigger
                          className="lux-select-trigger"
                          data-testid="select-current-size"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RING_SIZES.map((s) => (
                            <SelectItem
                              key={s}
                              value={String(s)}
                              data-testid={`opt-current-${s}`}
                            >
                              US {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Desired finger size">
                      <Select
                        value={form.desired_size}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, desired_size: v }))
                        }
                      >
                        <SelectTrigger
                          className="lux-select-trigger"
                          data-testid="select-desired-size"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RING_SIZES.map((s) => (
                            <SelectItem
                              key={s}
                              value={String(s)}
                              data-testid={`opt-desired-${s}`}
                            >
                              US {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Shank thickness">
                      <Select
                        value={form.shank_thickness}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, shank_thickness: v }))
                        }
                      >
                        <SelectTrigger
                          className="lux-select-trigger"
                          data-testid="select-shank-thickness"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {THICKNESS.map((t) => (
                            <SelectItem
                              key={t.id}
                              value={t.id}
                              data-testid={`opt-thick-${t.id}`}
                            >
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </DetailGroup>
                )}

                {form.services.includes("jump_ring") && (
                  <DetailGroup label="Jump Ring" testid="group-jump-ring">
                    <Field label="Jump ring size">
                      <Select
                        value={form.jump_ring_size}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, jump_ring_size: v }))
                        }
                      >
                        <SelectTrigger
                          className="lux-select-trigger"
                          data-testid="select-jump-ring-size"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {JUMP_RING_SIZES.map((j) => (
                            <SelectItem
                              key={j.id}
                              value={j.id}
                              data-testid={`opt-jr-${j.id}`}
                            >
                              {j.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </DetailGroup>
                )}

                {form.services.includes("stone_reset") && (
                  <DetailGroup label="Reset Stones" testid="group-stones">
                    <Field label="Number of stones">
                      <Input
                        type="number"
                        min={0}
                        max={50}
                        className="lux-input"
                        data-testid="input-stone-count"
                        value={form.stones.count}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            stones: {
                              ...f.stones,
                              count: Math.max(0, Number(e.target.value) || 0),
                            },
                          }))
                        }
                      />
                    </Field>
                    <Field label="Stone type">
                      <Select
                        value={form.stones.type}
                        onValueChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            stones: { ...f.stones, type: v },
                          }))
                        }
                      >
                        <SelectTrigger
                          className="lux-select-trigger"
                          data-testid="select-stone-type"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STONE_TYPES.map((s) => (
                            <SelectItem
                              key={s.id}
                              value={s.id}
                              data-testid={`opt-stone-${s.id}`}
                            >
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </DetailGroup>
                )}

                {form.services.includes("prong") && (
                  <DetailGroup label="Prong Rebuilding" testid="group-prong">
                    <Field label="Prongs to re-tip / rebuild">
                      <Input
                        type="number"
                        min={0}
                        max={20}
                        className="lux-input"
                        data-testid="input-prong-count"
                        value={form.prong_count}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            prong_count: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                      />
                    </Field>
                  </DetailGroup>
                )}

                {form.services.includes("chain_repair") && (
                  <DetailGroup label="Chain Repair" testid="group-chain">
                    <Field label="Break points to solder">
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        className="lux-input"
                        data-testid="input-chain-breaks"
                        value={form.chain_breaks}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            chain_breaks: Math.max(1, Number(e.target.value) || 1),
                          }))
                        }
                      />
                    </Field>
                  </DetailGroup>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={reset}
                    variant="outline"
                    className="rounded-none border-charcoal-900 text-charcoal-900 hover:bg-zinc-50 uppercase tracking-widest text-xs px-6 py-5"
                    data-testid="reset-btn"
                  >
                    Reset Form
                  </Button>
                  <div className="text-xs text-charcoal-400 flex items-center gap-2">
                    <Sparkles size={14} strokeWidth={1.5} className="text-champagne-500" />
                    Quote updates automatically as you adjust inputs.
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* RIGHT: sticky quote */}
          <aside className="lg:col-span-4">
            <div className="sticky top-6 space-y-4">
              <QuoteBreakdown
                quote={quote}
                loading={calcLoading}
                error={calcError}
                metalLabel={metalLabel}
              />
              <ScheduleDialog quote={quote} metalLabel={metalLabel} />
            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-xs text-charcoal-400">
            Estimates use live spot prices via metalpriceapi.com. Final pricing is
            confirmed at the bench after physical inspection.
          </div>
          <div className="overline">© Atelier Carat</div>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({ step, title, desc, inline }) {
  return (
    <div className={inline ? "flex items-end justify-between gap-6" : ""}>
      <div>
        <div className="overline">Step {step}</div>
        <h2 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">
          {title}
        </h2>
      </div>
      {desc ? (
        <p
          className={[
            "text-sm text-charcoal-500 max-w-md",
            inline ? "text-right" : "mt-2",
          ].join(" ")}
        >
          {desc}
        </p>
      ) : null}
    </div>
  );
}

function DetailGroup({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-4">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="lux-label">{label}</span>
      {children}
    </label>
  );
}
